import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { HYDRATE } from 'next-redux-wrapper'

import { RootState } from '~/redux/store'

import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'

import { LevelPageDetailsType, VccData } from '../types/bootcamp.types'
import { formatLevelActivities } from '../utils/bootcamp.utils'

interface VccSliceType extends LevelPageDetailsType, VccData {
  color: CategoryColorClasses
}

const initialState: VccSliceType = {
  module: undefined,
  moduleCount: 0,
  title: '',
  subjectName: '',
  complexityLevel: 1,
  whyShortTitle: '',
  hasWhyLevel: true,
  totalModules: 0,
  totalLevels: 0,
  levelsPageData: [],
  levelActivities: {},
  badgeImage: { srcKey: '' },
  badgeOutlineImage: { srcKey: '' },
  color: {
    bgClass: 'bg-purple',
    checkedBgClass: 'checked:bg-purple',
    textClass: 'text-purple',
    hex: '#7D38D6',
    borderClass: 'border-purple',
    outlineClass: 'outline-purple',
    ringClass: 'ring-purple',
  },
}
const vccDataSlice = createSlice({
  name: 'vcc-data',
  initialState,
  reducers: {
    setVccData: (state, action: PayloadAction<VccData>) => {
      const { name, module, moduleCount } = action.payload as VccData
      return {
        ...state,
        moduleCount,
        title: module?.name || '',
        subjectName: name || '',
        complexityLevel: module?.complexityLevel || 1,
        whyShortTitle: module?.shortTitle || '',
        hasWhyLevel: module?.hasWhyLevel ?? true,
        totalModules: moduleCount,
        totalLevels: module?.levelCount || 0,
        levelsPageData: module?.levelsPageData || [],
        levelActivities: module?.levelActivities ? formatLevelActivities(module?.levelActivities || []) : {},
        badgeImage: module?.badgeImage || { srcKey: '' },
        badgeOutlineImage: module?.badgeOutlineImage || { srcKey: '' },
      }
    },
    setVideoTopicColor: (state, action: PayloadAction<CategoryColorClasses>) => {
      if (action.payload) {
        return {
          ...state,
          color: { ...(action.payload as CategoryColorClasses) },
        }
      }

      return state
    },
  },

  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        ...action.payload.vcc,
      }
    })
  },
})

export const selectVideoTopicColor = (state: RootState): VccSliceType['color'] => {
  return state.vcc.color
}

export const selectVccPageTitle = (state: RootState): LevelPageDetailsType['title'] => {
  return state.vcc.title
}
export const selectVccSubjectName = (state: RootState): LevelPageDetailsType['subjectName'] => {
  return state.vcc.subjectName
}
export const selectVccComplexityLevel = (state: RootState): LevelPageDetailsType['complexityLevel'] => {
  return state.vcc.complexityLevel
}

export const selectVccWhyShortTitle = (state: RootState): LevelPageDetailsType['whyShortTitle'] => {
  return state.vcc.whyShortTitle
}

export const selectVccHasWhyLevel = (state: RootState): LevelPageDetailsType['hasWhyLevel'] => {
  return state.vcc.hasWhyLevel
}
// this one is not used
export const selectVccTotalModules = (state: RootState): VccData['moduleCount'] => {
  return state.vcc.moduleCount
}

export const selectVccPageTotalLevels = (state: RootState): LevelPageDetailsType['totalLevels'] => {
  return state.vcc.totalLevels
}

export const selectVccPageLevelsData = (state: RootState): LevelPageDetailsType['levelsPageData'] => {
  return state.vcc.levelsPageData
}

export const selectVccLevelActivities = (state: RootState): LevelPageDetailsType['levelActivities'] => {
  return state.vcc.levelActivities
}

export const selectVccSubBadgeImage = (state: RootState): LevelPageDetailsType['badgeImage'] => {
  return state.vcc.badgeImage
}

export const selectVccSubBadgeOutlineImage = (state: RootState): LevelPageDetailsType['badgeOutlineImage'] => {
  return state.vcc.badgeOutlineImage
}
export const { setVccData, setVideoTopicColor } = vccDataSlice.actions

export default vccDataSlice.reducer
